import { useEffect, useRef } from "react";
import {
  useConnectionStore,
  ConnectionStatus,
} from "@/features/tutor/store/useConnectionStore";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import { debugLog } from "@/utils/debugLog";

const DISCONNECTED_OVERLAY_DELAY_MS = 10000;

export const useConnectionStatus = () => {
  const wsConnected = useWebsocketStore((state) => state.wsConnected);
  const status = useConnectionStore((state) => state.status);
  const showDisconnectedOverlay = useConnectionStore(
    (state) => state.showDisconnectedOverlay,
  );
  const wasEverDisconnected = useConnectionStore(
    (state) => state.wasEverDisconnected,
  );

  const overlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prevWsConnectedRef = useRef<boolean>(wsConnected);

  const clearTimer = () => {
    if (overlayTimerRef.current) {
      clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = null;
    }
  };

  const handleDisconnect = () => {
    const store = useConnectionStore.getState();
    if (store.status === ConnectionStatus.CONNECTED) {
      store.markDisconnected();

      overlayTimerRef.current = setTimeout(() => {
        useConnectionStore.getState().setShowDisconnectedOverlay(true);
        overlayTimerRef.current = null;
      }, DISCONNECTED_OVERLAY_DELAY_MS);
    }
  };

  const handleConnect = () => {
    clearTimer();
    useConnectionStore.getState().markConnected();
  };

  useEffect(() => {
    if (wsConnected && !prevWsConnectedRef.current) {
      handleConnect();
    } else if (!wsConnected && prevWsConnectedRef.current) {
      handleDisconnect();
    }

    prevWsConnectedRef.current = wsConnected;
  }, [wsConnected]);

  // Only reconnect (handleConnect) and unmount cancel the overlay timer. A
  // socket that closes after an offline event must not kill the timer it started.
  useEffect(() => clearTimer, []);

  useEffect(() => {
    const onOffline = () => {
      // Only a live socket has anything to lose. Before the first connect the
      // overlay could only come down on an onopen that a failed boot never
      // reaches, so it stayed up over the page for good.
      if (useWebsocketStore.getState().wsConnected) {
        handleDisconnect();
      }
    };

    const onOnline = () => {
      debugLog("[ConnectionStatus] Browser online, triggering WS reconnect");
      if (useWebsocketStore.getState().wsConnected) {
        handleConnect();
      } else if (typeof (window as any).wsReconnect === "function") {
        (window as any).wsReconnect();
      }
    };

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  return {
    status,
    isConnected: status === ConnectionStatus.CONNECTED,
    isDisconnected: status === ConnectionStatus.DISCONNECTED,
    showDisconnectedOverlay,
    wasEverDisconnected,
  };
};
